import { produce } from 'immer';
import { createReducer, on, Action } from '@ngrx/store';
import {
  loadConfiguracionDePago,
  loadPago,
  savePago,
  saveConfiguracionDePago,
  registerPagoFailed,
  clearError,
  changeRenovacionAutomatica,
  changeMedioDePago,
  errorEnPago,
  setInitialPagoState,
  registerOtroMedioDePagoSuccess,
  registerOtroMedioDePagoFailed,
  loadConfiguracionDePagoFailed,
  saveTenant,
  saveProductNameFromUrl,
  idPagoInvalidFromUrl,
  consultarEstadoPagoFailed,
} from 'src/app/store/actions/pago.action';
import {
  getInitialPagoState,
  PagoState,
  initialErrorState,
} from 'src/app/store/state/pago.state';
import { ConfiguracionDePago } from 'src/app/pago/models/ConfiguracionDePago.model';
import { Descuento } from 'src/app/pago/models/Descuento.model';
import { MedioDePago } from 'src/app/pago/enums/MedioDePago.enum';
import { ManagerErrorPagoService } from 'src/app/pago/services/manager-error/manager-error-pago.service';

const reducer = createReducer(
  getInitialPagoState(),
  on(loadConfiguracionDePago, loadPago, (state, { idPago }) => ({
    ...state,
    pago: {
      ...state.pago,
      idPago,
    },
    estadoDePagoLoaded: false,
  })),
  on(saveConfiguracionDePago, (state, { configuracionDePago }) =>
    initConfiguracionDePago(state, configuracionDePago)
  ),
  on(savePago, (state, { pago }) => ({
    ...state,
    pago: {
      ...state.pago,
      ...pago,
    },
    estadoDePagoLoaded: true,
  })),
  on(registerPagoFailed, (state, { error }) =>
    ManagerErrorPagoService.getError(state, error)
  ),
  on(errorEnPago, (state, { error }) => ({
    ...state,
    error: {
      ...error,
    },
  })),
  on(saveTenant, (state, { tenant }) => ({
    ...state,
    tenant,
  })),
  on(clearError, state => ({
    ...state,
    error: {
      ...initialErrorState,
    },
  })),
  on(changeRenovacionAutomatica, (state, { activarRenovacionAutomatica }) =>
    updateRenovacionAutomatica(state, activarRenovacionAutomatica)
  ),
  on(changeMedioDePago, (state, { medioDePago }) =>
    updateMedioDePago(state, medioDePago)
  ),
  on(setInitialPagoState, state => updateInitialPagoState(state)),
  on(
    registerOtroMedioDePagoSuccess,
    (state, { urlRedirectOtroMedioDePago }) => ({
      ...state,
      urlRedirectOtroMedioDePago,
    })
  ),
  on(registerOtroMedioDePagoFailed, (state, { error }) =>
    ManagerErrorPagoService.getErrorOtroMedioDePago(state, error)
  ),
  on(loadConfiguracionDePagoFailed, (state, { error }) =>
    ManagerErrorPagoService.getErrorConfiguracionDePago(state, error)
  ),
  on(consultarEstadoPagoFailed, (state, { error }) =>
    ManagerErrorPagoService.getErrorEstadoPago(state, error)
  ),
  on(saveProductNameFromUrl, (state, { productName }) => ({
    ...state,
    productNameFromUrl: productName,
  })),
  on(idPagoInvalidFromUrl, (state, { error }) =>
    ManagerErrorPagoService.getErrorConfiguracionDePago(state, error)
  )
);

function updateRenovacionAutomatica(
  state: PagoState,
  activarRenovacionAutomatica: boolean
): PagoState {
  state.pago.renovacionAutomatica = activarRenovacionAutomatica;
  return updateDescuentosAAplicar(state);
}

function updateMedioDePago(
  state: PagoState,
  medioDePago: MedioDePago
): PagoState {
  state.pago.medioPago = medioDePago;
  if (medioDePago === MedioDePago.OTRO_MEDIO_DE_PAGO) {
    state.pago.renovacionAutomatica = false;
  }
  return updateDescuentosAAplicar(state);
}

function updateDescuentosAAplicar(state: PagoState): PagoState {
  const subTotal = state.configuracionDePago.valor;
  const descuentosAplicados = state.configuracionDePago.listaDeDescuentos;

  const totalDescuentos =
    descuentosAplicados && descuentosAplicados.length
      ? getTotalDescuentos(descuentosAplicados)
      : 0;
  const total = subTotal - totalDescuentos;

  state.pago.descuentosAplicados = descuentosAplicados;
  state.pago.totalDescuentos = totalDescuentos;
  state.pago.subTotal = subTotal;
  state.pago.total = total;
  return state;
}

function initConfiguracionDePago(
  state: PagoState,
  configuracionDePago: ConfiguracionDePago
): PagoState {
  state.pago.idProducto = configuracionDePago.idProducto;
  state.pago.encuestaSatisfaccion = configuracionDePago.encuestaSatisfaccion;
  state.configuracionDePago = configuracionDePago;
  return updateDescuentosAAplicar(state);
}

function getTotalDescuentos(descuentos: Descuento[]): number {
  return descuentos.reduce((accumulator: Descuento, descuento: Descuento) => {
    return {
      codigo: '',
      descripcion: '',
      porcentaje: 0,
      totalDescuento: accumulator.totalDescuento + descuento.totalDescuento,
    };
  }).totalDescuento;
}

export function pagoReducer(state = getInitialPagoState(), action): PagoState {
  return produce((draft: PagoState, formAction: Action) => {
    return reducer(draft, formAction);
  }, getInitialPagoState())(state, action);
}

function updateInitialPagoState(state: PagoState): PagoState {
  return { ...getInitialPagoState() };
}
